import React from "react";
import { Button } from "react-bootstrap";

import Loader from "./Loader";

// Screens are code-split with React.lazy, so each route is a separately
// hashed .js chunk. A deploy replaces those hashes, which means a tab that was
// already open is holding a map to filenames the server no longer has: the
// next navigation requests a missing chunk, the lazy import rejects, and
// without a boundary that rejection unmounts the whole app to a blank page.
//
// Reloading fixes it because index.html is re-fetched and points at the new
// hashes. We do that automatically, but only once and only for chunk
// failures, so an offline user or an ordinary render bug can't put the app
// into a reload loop.

const RELOAD_MARKER = "chunkReloadAt";
// If a reload was already attempted this recently, the reload clearly did not
// help, so stop and let the user decide instead of spinning.
const RELOAD_COOLDOWN_MS = 30000;

// Browsers word this differently: webpack throws ChunkLoadError, Chrome says
// "Failed to fetch dynamically imported module", Safari "Importing a module
// script failed", Firefox "error loading dynamically imported module".
export const isChunkLoadError = (error) => {
  if (!error) return false;
  const name = String(error.name || "");
  const message = String(error.message || "");
  return (
    name === "ChunkLoadError" ||
    /loading chunk \S+ failed/i.test(message) ||
    /loading css chunk/i.test(message) ||
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message)
  );
};

// sessionStorage throws in some privacy modes; never let bookkeeping be the
// thing that breaks the recovery path.
const readMarker = () => {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_MARKER)) || 0;
  } catch (e) {
    return 0;
  }
};

const writeMarker = () => {
  try {
    window.sessionStorage.setItem(RELOAD_MARKER, String(Date.now()));
  } catch (e) {
    // Ignore: worst case we simply do not auto-reload.
  }
};

const canAutoReload = () => Date.now() - readMarker() >= RELOAD_COOLDOWN_MS;

const initialState = { hasError: false, isChunkError: false, willReload: false };

class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { ...initialState, lastResetKey: props.resetKey };
  }

  static getDerivedStateFromError(error) {
    const isChunkError = isChunkLoadError(error);
    return {
      hasError: true,
      isChunkError,
      // Decided here so render can show the loader rather than flashing an
      // error message for the moment before the reload takes effect.
      willReload: isChunkError && canAutoReload(),
    };
  }

  // Clear the error when the route changes, so navigating away recovers.
  // Done here rather than with a key on this component: a key would remount
  // the whole subtree on every navigation, which would also remount screens
  // on a bare param change (/users/1 -> /users/2) that React Router
  // otherwise updates in place.
  static getDerivedStateFromProps(props, state) {
    if (props.resetKey !== state.lastResetKey) {
      return { ...initialState, lastResetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error, errorInfo) {
    console.error("Route failed to render", error, errorInfo);

    if (!isChunkLoadError(error) || !canAutoReload()) return;

    writeMarker();
    window.location.reload();
  }

  handleRetry = () => {
    // A manual retry is the user asking for it, so bypass the cooldown.
    writeMarker();
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    // A reload is already on its way; showing the normal loading state avoids
    // flashing a failure message the user has no reason to act on.
    if (this.state.willReload) return <Loader />;

    return (
      <div style={{ textAlign: "center", padding: "3rem 1rem" }}>
        <h5>
          {this.state.isChunkError
            ? "This page could not be loaded"
            : "Something went wrong on this page"}
        </h5>
        <p style={{ color: "grey", fontSize: "0.9rem" }}>
          {this.state.isChunkError
            ? "The app was updated while this tab was open. Reloading will pick up the latest version."
            : "Please reload the page and try again."}
        </p>
        <Button onClick={this.handleRetry}>Reload</Button>
      </div>
    );
  }
}

export default ChunkErrorBoundary;
